/**
 * Where the sandbox action buttons live — the header on a wide screen, a
 * bottom-right widget on a narrow one.
 *
 * 🔴 THE HEADER IS NOT A PLACE THREE BUTTONS CAN LIVE BELOW `lg`.
 * The host's header row is `flex … flex-shrink-0` with every other child
 * `flex-shrink-0` too, so a row that does not fit does not wrap — it overflows,
 * and because the header sits inside a `fixed right-0` wrapper the overflow is
 * clipped by the viewport rather than scrolled to. Below `lg` the row is already
 * carrying the search field, the help icon, the upgrade button, the Nexa AI pill,
 * notifications and the account switcher, so the tour module's own group was the
 * part that disappeared: present in the DOM, painted off-screen. Shrinking the
 * pills further only postpones that (and was already down to icon-only).
 *
 * 🔴 AND THE HEADER'S SEARCH FIELD IS PAINTED OVER THE ROW.
 * The host's desktop search block is `absolute … z-10`, so it is NOT in the flex
 * flow: the row is laid out as though the search were not there and the search
 * then paints on top of whatever landed underneath it. Measured in the sandbox at
 * the default sidebar width, the search box ends at x≈604 while this group starts
 * at x≈454 (1440px), 344 (1280px) and 328 (1024px) — so one or two of the three
 * buttons were hidden BEHIND the search field at every desktop width, not only on
 * a narrow screen. That is a host-layout bug this module is not allowed to fix
 * (the header is shared by every page), so the module works around it instead.
 *
 * 🔴 THEREFORE THE PLACEMENT IS MEASURED, NOT BREAKPOINTED.
 * A media query cannot know any of what decides whether the group fits: how wide
 * the host's own row is right now, whether the sidebar is collapsed (which hands
 * back ~200px), whether the plan-credits bar has finished loading, or where the
 * absolutely-positioned search box ends. So the module tries the header first and
 * HIT-TESTS the result — labelled pills, then icon-only pills, then, only if even
 * the icons are covered or off-screen, the bottom-right widget. Each step is the
 * same three buttons, just narrower, so the cascade always ends somewhere usable.
 *
 * The widget is the last resort and looks like the host's own mobile Nexa AI
 * launcher: a collapsed floating button that expands into a labelled menu. A
 * corner widget cannot be squeezed out by a neighbour, and the labels come back
 * because the widget has room for them.
 *
 * 🔴 THE CHILDREN ARE MOUNTED IN EXACTLY ONE PLACE AND STAY MOUNTED.
 * Collapsing the widget hides the menu with `visibility` — it does NOT unmount
 * it. `DemoBookingButton` owns its dialog's open state, so unmounting the
 * collapsed menu would close the demo dialog the moment the widget collapsed
 * behind it. (`visibility: hidden` also takes the buttons out of the tab order,
 * so the hidden menu is not reachable by keyboard either.)
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { HOST } from '../engine/anchors';
import { useTourGuide } from '../TourGuideProvider';
import { HeaderSlot } from './HeaderSlot';

/**
 * The floor below which the header is not even tried.
 *
 * `lg` (1024px): below it the host has hidden most of its own row (credits bar,
 * Nexa pill, account switcher) and moved its launcher into the corner, which is a
 * clear statement that the row has no room to lend — and probing a phone header
 * would put three buttons into it, however briefly. Above the floor the fit is
 * measured rather than assumed; see the file header.
 */
const HEADER_FLOOR_MEDIA = '(min-width: 1024px)';

/**
 * Narrowing steps tried in the header, widest first. `null` means "give up and
 * use the widget" — it is the sentinel the cascade returns, never a variant.
 */
const HEADER_VARIANTS = ['labels', 'icons'] as const;
type HeaderVariant = (typeof HEADER_VARIANTS)[number];

/** How far inside each button edge the hit test samples, in px. */
const HIT_TEST_INSET = 3;

/**
 * How long to wait before re-measuring after a blocked probe.
 *
 * A backstop only: a Radix dialog closing is caught by an attribute observer, so
 * this exists for a scrim that does not use `aria-hidden`. Long enough not to spin
 * behind a dialog someone leaves open.
 */
const BLOCKED_RETRY_MS = 700;

/** Resting distance from the viewport edges, in px. */
const DOCK_INSET = 20;
/** Vertical gap left between the widget and a host launcher it stacks above. */
const DOCK_STACK_GAP = 12;
/**
 * How far from the bottom-right corner a fixed element still counts as "in the
 * corner" and therefore something to stack above. Generous enough to catch a
 * raised launcher (the host lifts its own to `bottom: 96px` over pagination).
 */
const CORNER_ZONE_PX = 200;

/**
 * How far the widget can be dragged from its home corner, in pixels.
 *
 * 🔴 A RADIUS, NOT THE WHOLE VIEWPORT — the widget is nudge-able, not free-floating.
 * It exists to be got out of the way of whatever it happens to be covering (a
 * pagination row, a chart legend, the host's own launcher), and that is always a
 * short move. Letting it be parked anywhere would mean a visitor could lose it
 * behind a scroll container or drop it somewhere the next page has nothing to do
 * with, with no way back except a reload — and because the corner is also where the
 * placement cascade measures, a widget parked mid-screen would stop reading as "the
 * sandbox controls live in the corner" at all.
 */
const DRAG_RADIUS_PX = 220;

/**
 * Movement before a press counts as a drag rather than a click.
 *
 * 🔴 WITHOUT IT THE FAB STOPS OPENING. Every real click carries a pixel or two of
 * pointer movement, so treating any movement as a drag would swallow the tap that
 * opens the menu — and treating none of it as a drag would open the menu at the end
 * of every drag. 6px is the usual platform threshold and comfortably above the
 * jitter of a deliberate click.
 */
const DRAG_THRESHOLD_PX = 6;

/** Keep this much of the widget inside the viewport on every side. */
const DRAG_VIEWPORT_MARGIN = 8;

function useMediaMatch(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const sync = () => setMatches((prev) => (prev === mq.matches ? prev : mq.matches));
    sync();
    // `addListener` is the deprecated form, kept for older WebKit on iOS.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, [query]);

  return matches;
}

/**
 * Is a MODAL holding the page inert?
 *
 * 🔴 THIS IS NOT THE SAME QUESTION AS "does the group fit", AND CONFLATING THE
 * TWO WAS A REAL BUG. Radix marks every other top-level element `aria-hidden` /
 * `data-aria-hidden` while a dialog is open, and its overlay paints across the
 * whole viewport — so a hit test run while the sandbox's own "What's New" dialog
 * is up fails on ALL THREE buttons, the cascade concludes there is no room, and
 * the group moves to the widget. Nothing re-measures when the dialog closes, so a
 * transient modal PERMANENTLY demoted the buttons until the next resize.
 *
 * A blocked probe therefore returns no verdict at all: the previous placement is
 * kept and the measurement is retried once the page is interactive again.
 */
function isModalBlocking(group: HTMLElement): boolean {
  return Boolean(group.closest('[data-aria-hidden="true"], [aria-hidden="true"]'));
}

/**
 * Is this element an overlay rather than page chrome?
 *
 * The geometric half of the check above, for a backdrop that does not go through
 * Radix's `aria-hidden` bookkeeping. Anything fixed and covering essentially the
 * whole viewport is a scrim, not a neighbour competing for space.
 */
function isViewportOverlay(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.9) return false;
  const position = getComputedStyle(el).position;
  return position === 'fixed' || position === 'absolute';
}

/**
 * What the measurement can conclude.
 *
 * `fits` — usable here, at `variant`.
 * `no-room` — genuinely covered or clipped by the host's own row. Use the widget.
 * `blocked` — the page is not interactive (a modal is up). NO verdict; keep the
 *             current placement and ask again later.
 */
type ProbeResult =
  | { verdict: 'fits'; variant: HeaderVariant }
  | { verdict: 'no-room' }
  | { verdict: 'blocked' };

/**
 * Is this group usable where the host put it, at its current width?
 *
 * 🔴 A HIT TEST, NOT AN ARITHMETIC ONE. Width maths cannot see the host's
 * absolutely-positioned search field painting over the row (see the file header) —
 * the group's rect is perfectly inside the header while the user cannot see or
 * click a thing. `elementFromPoint` asks the browser the same question the user's
 * finger does, so it catches being covered, being clipped and being pushed
 * off-screen with one check.
 *
 * Sampled at each button's centre AND just inside both of its horizontal edges,
 * because the search box covers the group from the LEFT: a centre-only probe
 * passes for a button that is half hidden.
 */
function measureUsable(group: HTMLElement): 'usable' | 'covered' | 'blocked' {
  const rect = group.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return 'covered';
  // Off-screen entirely, or overflowing the viewport the host's row cannot wrap in.
  if (rect.left < 0 || rect.right > window.innerWidth) return 'covered';

  const buttons = group.querySelectorAll<HTMLElement>('.tour-guide-launcher');
  // No buttons at all is not "usable" — it is the disabled module, and the caller
  // decides that case before ever asking.
  if (buttons.length === 0) return 'covered';

  for (const button of buttons) {
    const r = button.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return 'covered';
    if (r.left < 0 || r.right > window.innerWidth) return 'covered';

    const y = r.top + r.height / 2;
    for (const x of [r.left + HIT_TEST_INSET, r.left + r.width / 2, r.right - HIT_TEST_INSET]) {
      const top = document.elementFromPoint(x, y);
      // `contains` covers the icon and the label inside the button; the group check
      // covers a tooltip bubble of a neighbouring button overlapping this one.
      if (top && group.contains(top)) continue;
      // A scrim over the whole page says nothing about how much room the row has.
      if (top && isViewportOverlay(top)) return 'blocked';
      return 'covered';
    }
  }

  return 'usable';
}

/**
 * Try each narrowing step in the header and keep the first that survives the hit
 * test.
 *
 * 🔴 THE VARIANT IS WRITTEN STRAIGHT TO THE DOM, not through React state. The
 * measurement has to happen BETWEEN two candidate widths, which means the class
 * must change and layout must settle several times inside ONE commit — a state
 * update per step would paint each intermediate step and take a frame to do it.
 * React never sets this attribute in JSX, so writing it here cannot be clobbered
 * by a re-render.
 */
function probeHeader(group: HTMLElement): ProbeResult {
  if (isModalBlocking(group)) return { verdict: 'blocked' };

  for (const variant of HEADER_VARIANTS) {
    group.dataset.variant = variant;
    // Reading a layout property flushes the style change synchronously, so the
    // hit test below sees the new width rather than the previous variant's.
    void group.offsetWidth;
    const result = measureUsable(group);
    if (result === 'usable') return { verdict: 'fits', variant };
    // A scrim covers every variant equally — narrowing further proves nothing.
    if (result === 'blocked') return { verdict: 'blocked' };
  }
  return { verdict: 'no-room' };
}

/**
 * Bumps whenever something that could change the answer happened.
 *
 * 🔴 IT DELIBERATELY DOES NOT WATCH OUR OWN GROUP. Every source here is host
 * chrome: the viewport resizing, the header itself resizing (which is how a
 * sidebar collapse hands ~200px back), the host's row gaining or losing a child
 * (the plan-credits bar arrives a second or two after the shell), and web fonts
 * settling (which moves label widths). Watching our own subtree would make the
 * cascade's own class changes retrigger it.
 */
function useProbeSignal(active: boolean): { signal: number; bump: () => void } {
  const [signal, setSignal] = useState(0);
  const bumpRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;

    let raf = 0;
    const bump = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setSignal((n) => n + 1));
    };
    bumpRef.current = bump;

    window.addEventListener('resize', bump);

    const slot = document.querySelector(HOST.headerSlot);
    const header = slot?.closest('header') ?? null;
    // The row the slot sits in — where the host's own children come and go.
    const row = slot?.parentElement ?? null;

    const headerResize = header ? new ResizeObserver(bump) : null;
    headerResize?.observe(header as Element);

    const rowMutations = row
      ? new MutationObserver((records) => {
          // A record whose target is inside the slot is this module mounting or
          // unmounting itself — never a reason to re-decide.
          const fromHost = records.some((r) => !slot || !slot.contains(r.target));
          if (fromHost) bump();
        })
      : null;
    rowMutations?.observe(row as Node, { childList: true, subtree: true });

    /**
     * 🔴 THE MODAL SIGNAL. A dialog opening or closing is the one page change that
     * alters the answer without touching the header's own size or children: while
     * it is up the row is inert and unmeasurable, and when it goes the row is
     * usable again. Radix toggles exactly these two attributes on every other
     * top-level element, so this is both the cheapest and the most precise trigger
     * available — and without it a transient dialog stranded the buttons in the
     * widget for the rest of the session.
     *
     * 🔴 IT MUST IGNORE OUR OWN SUBTREE, and skipping that filter is what made the
     * widget refuse to open at 1024px and above. The dock's menu carries
     * `aria-hidden` while collapsed, so the very act of opening it REMOVED an
     * observed attribute inside `document.body` → `bump()` → the cascade re-ran →
     * `setTryHeader(wideEnough)` put the group back in the header → `dockable` went
     * false → the `!dockable` effect called `close()` → the menu's `aria-hidden`
     * came back → another mutation. Click, and nothing happened. Below 1024
     * `wideEnough` is false, so `setTryHeader` never flipped and the same click
     * worked — which is exactly the width-dependent "it doesn't open" that was
     * reported. Same rule as `rowMutations` above: a record from our own DOM is
     * this module reacting, never a reason to re-decide.
     */
    const ownRecord = (target: Node): boolean => {
      const el = target instanceof Element ? target : target.parentElement;
      if (!el) return false;
      return Boolean(el.closest('[data-tour-dock]')) || Boolean(slot?.contains(el));
    };
    const modalState = new MutationObserver((records) => {
      if (records.some((r) => !ownRecord(r.target))) bump();
    });
    modalState.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-aria-hidden', 'aria-hidden'],
    });

    // Fonts change label widths after first paint, and there is no event for that
    // on the elements themselves.
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) bump();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', bump);
      headerResize?.disconnect();
      rowMutations?.disconnect();
      modalState.disconnect();
      bumpRef.current = () => {};
    };
  }, [active]);

  // Stable identity, so an effect can depend on it without re-subscribing.
  const bump = useCallback(() => bumpRef.current(), []);
  return { signal, bump };
}

/**
 * Things already fixed to the bottom of the viewport that the widget has to clear.
 *
 * Both selectors are ones the module ALREADY knows (the tour steps point at the
 * host's launcher; the engine suppresses the badge for the duration of a tour), so
 * this adds no new knowledge of the host — and a selector that matches nothing
 * simply leaves the widget at its resting inset.
 *
 * The badge is bottom-LEFT, so on a wide screen it is not in the way and the
 * measurement below skips it on position alone. On a phone it is nearly as wide as
 * the viewport and does reach this corner, which is exactly when it must be
 * cleared: it is painted at the maximum z-index and accepts pointer events, so a
 * widget underneath it is not merely overlapped — it is unclickable.
 */
const CORNER_OCCUPANTS = [HOST.headerAiLauncher, ...HOST.suppressDuringTour];

/**
 * The widget's distance from the bottom of the viewport.
 *
 * 🔴 MEASURED, NOT ASSUMED. The host puts its own launcher in this corner on
 * mobile and MOVES it (up, over pagination) and REMOVES it (an open chat pane, an
 * open conversation) depending on the page. A hard-coded offset is wrong on at
 * least one of those states — either overlapping the host's button or floating in
 * a gap where it used to be. Reading the live rect is right in all of them.
 */
function useDockBottom(active: boolean): number {
  const [bottom, setBottom] = useState(DOCK_INSET);

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;

    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        let highestTop = Infinity;

        for (const selector of CORNER_OCCUPANTS) {
          document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
            const r = el.getBoundingClientRect();
            // A `display: none` element (the host's launcher is class-hidden per
            // breakpoint, not unmounted) measures 0×0.
            if (r.width <= 0 || r.height <= 0) return;
            // The launcher selector also matches its DESKTOP copy, which lives in
            // the header and must not move the widget.
            if (getComputedStyle(el).position !== 'fixed') return;
            if (r.bottom < vh - CORNER_ZONE_PX) return;
            // Not in THIS corner — the sandbox badge on a wide screen.
            if (r.right < vw - CORNER_ZONE_PX) return;
            highestTop = Math.min(highestTop, r.top);
          });
        }

        const next =
          highestTop === Infinity
            ? DOCK_INSET
            : Math.max(DOCK_INSET, Math.round(vh - highestTop + DOCK_STACK_GAP));
        setBottom((prev) => (prev === next ? prev : next));
      });
    };

    measure();
    window.addEventListener('resize', measure);
    // The host raises its launcher on scroll (pagination entering the corner).
    const scrollRoot = document.getElementById('app-main-scroll');
    scrollRoot?.addEventListener('scroll', measure, { passive: true });
    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // The launcher is raised by swapping a class, with no node added or removed.
      attributes: true,
      attributeFilter: ['class', 'data-nexa-fab-raised'],
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      scrollRoot?.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [active]);

  return active ? bottom : DOCK_INSET;
}

export function ActionDock({ children }: { children: React.ReactNode }) {
  const tour = useTourGuide();
  const wideEnough = useMediaMatch(HEADER_FLOOR_MEDIA);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /* ---------------------------------------------------------------- *
   * Nudge state.
   * ---------------------------------------------------------------- *
   * 🔴 DECLARED UP HERE, ABOVE THE PLACEMENT CASCADE, BECAUSE THE CASCADE READS
   * `dragging` IN ITS DEPENDENCY ARRAY. A dependency array is built during RENDER,
   * so a `const` declared further down is still in its temporal dead zone at that
   * point and the whole component throws "Cannot access 'dragging' before
   * initialization" — which takes the header group down with it, not just the
   * widget. TypeScript does not catch it: referencing a later `const` from inside a
   * closure is legal, and the deps array only looks like one.
   *
   * The handlers that USE this state stay next to the render code they belong to,
   * further down — they are only ever called after the whole component body has run.
   */
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  /**
   * A pointer is DOWN on the handle — set before the drag threshold is reached, and
   * the flag the placement cascade freezes on.
   *
   * 🔴 IT CANNOT WAIT FOR THE THRESHOLD, and that is why a drag did nothing at all
   * at 1024px and above. The cascade re-offers the header on every prober bump,
   * which UNMOUNTS the widget for that commit — taking the button that holds the
   * pointer capture with it, so `pointermove` stops being delivered and the gesture
   * dies where it started. Freezing on `dragging` left the pre-threshold window
   * (the first few pixels of every drag) unprotected, which was enough.
   */
  const [pressing, setPressing] = useState(false);
  /** Past the threshold: drives the cursor and shadow, and suppresses the click. */
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<
    { id: number; startX: number; startY: number; fromX: number; fromY: number; moved: boolean } | null
  >(null);
  /**
   * 🔴 THE POINTER TOGGLES ON `pointerup`, NOT ON `click`, and the keyboard keeps
   * `click`. Splitting them is what finally made the widget open at 1024px and
   * above.
   *
   * The failure was subtle: `pointerup` released the cascade freeze, the cascade
   * runs in a LAYOUT effect (synchronously, pre-paint, inside that same commit), it
   * re-offered the header, and React removed the button from the DOM — all BEFORE
   * the browser got to dispatch the `click` that follows every `pointerup`. The
   * event had no target left to reach, so the toggle simply never ran. Nothing was
   * broken about the handler; the element it was attached to no longer existed.
   *
   * Toggling on `pointerup` also means `open` is already true when the freeze lifts,
   * and the freeze covers `open` too — so the widget cannot unmount while its own
   * menu is up. `click` still handles Enter and Space, which arrive with
   * `detail === 0` and no pointer events at all; a click synthesised by a real
   * pointer carries `detail >= 1` and is ignored here, which is also what stops the
   * click at the end of a drag from opening the menu.
   */
  /**
   * 🔴 STATE, NOT A REF. `HeaderSlot` re-portals whenever the host's slot element
   * is replaced (the header remounts across route and auth changes), and a portal
   * that changes target UNMOUNTS the subtree and mounts a fresh one — a brand-new
   * `<div>` with none of the measured `data-variant` on it. A ref would hold the
   * new node silently, the layout effect's deps would not have changed, and the
   * group would sit in the header unmeasured and covered. As state, the node's
   * identity is a dependency, so a remount re-runs the cascade.
   */
  const [groupEl, setGroupEl] = useState<HTMLDivElement | null>(null);

  // 🔴 The module is mounted (disabled) in the real app too, where every child
  // renders null. Without the context there is nothing to dock, and a floating
  // widget wrapping three nulls would appear on every non-sandbox page. Fall
  // back to the header slot — identical to what a disabled module does today,
  // and skip the measuring entirely: there is nothing in the group to measure.
  const enabled = Boolean(tour);

  const { signal: probe, bump: reprobe } = useProbeSignal(enabled && wideEnough);

  /**
   * `true` while the header is being tried. Reset to the floor's answer on every
   * probe, so a window that got wider — or a sidebar that just collapsed — gets
   * the header offered again rather than being stuck in the widget.
   */
  const [tryHeader, setTryHeader] = useState(true);
  /**
   * The last verdict the measurement actually reached.
   *
   * 🔴 NEEDED BECAUSE THE RESET ABOVE DESTROYS THE CURRENT ANSWER. Every probe
   * optimistically offers the header again, so by the time a measurement comes back
   * `blocked` (a modal is up — see `probeHeader`), the state no longer remembers
   * that the group belonged in the widget. Without this, a dialog opening while the
   * buttons were docked would flip them into the header and leave them there.
   */
  const verdictRef = useRef<'header' | 'dock'>('header');
  // 🔴 A LAYOUT effect, like the cascade below, so offering the header and deciding
  // against it happen in ONE pre-paint sequence. From an ordinary effect the reset
  // lands after a paint, and the rejected attempt is on screen for a frame — the
  // buttons visibly blink into the top bar on every resize.
  useLayoutEffect(() => {
    /**
     * 🔴 NOT WHILE THE VISITOR IS USING IT. Re-offering the header sets `dockable`
     * false for a commit, which trips the `!dockable` effect below and closes the
     * menu — so any unrelated page change that bumps the prober (a toast, a tooltip,
     * a dialog anywhere) would shut a menu the visitor had just opened, or unmount
     * the widget mid-drag and kill the pointer capture. The guard is self-healing
     * rather than sticky: closing the menu or lifting the pointer flips
     * `open`/`pressing`, which is a dependency here, so the placement is re-decided
     * immediately afterwards.
     */
    if (open || pressing) return;
    setTryHeader(wideEnough);
  }, [wideEnough, probe, open, pressing]);

  /**
   * 🔴 A LAYOUT EFFECT, so the whole cascade — mount in the header, measure both
   * variants, fall back to the widget — happens inside one commit, BEFORE the
   * browser paints. In an ordinary effect the rejected header attempt is painted
   * first, and the buttons visibly flash into the top bar and jump to the corner
   * on every resize.
   */
  useLayoutEffect(() => {
    if (!enabled || !tryHeader || !groupEl) return;
    const result = probeHeader(groupEl);

    /**
     * Blocked: a modal is up, so nothing was learned. Put the last real verdict
     * back and ask again — the attribute observer in `useProbeSignal` fires the
     * moment the dialog closes, and the timer is only a backstop for a scrim that
     * does not go through Radix. The group stays `data-probing` (invisible) while
     * this is unresolved, which is correct: it has not been measured, and a modal
     * is covering the row anyway.
     */
    if (result.verdict === 'blocked') {
      if (verdictRef.current === 'dock') setTryHeader(false);
      const retry = window.setTimeout(reprobe, BLOCKED_RETRY_MS);
      return () => window.clearTimeout(retry);
    }

    if (result.verdict === 'no-room') {
      verdictRef.current = 'dock';
      setTryHeader(false);
      return;
    }

    verdictRef.current = 'header';
    // Decided: reveal it. Removed imperatively, in the same pre-paint pass as the
    // measurement — going through state would cost a render, and the group would be
    // invisible for that frame every time the cascade re-ran.
    delete groupEl.dataset.probing;
  }, [enabled, tryHeader, probe, groupEl, children, reprobe]);

  const dockable = enabled && !tryHeader;

  const running = Boolean(tour?.run.running || tour?.task.running);
  const dockBottom = useDockBottom(dockable);

  /* ---------------------------------------------------------------- *
   * The attention wave.
   * ---------------------------------------------------------------- *
   * A sandbox visitor who never notices the launcher never takes the tour, and
   * noticing it is genuinely hard: in the header it is one small pill in a row that
   * already carries the search field, the credits bar, the upgrade button, the Nexa
   * pill, notifications and the account switcher, and in the widget it is a 48px
   * circle in a corner next to the host's own launcher. So it waves — a pair of
   * staggered rings ripples out of it (see `tour-guide.css`) until the visitor
   * answers.
   *
   * 🔴 ONLY OPENING THE WIDGET RETIRES IT — FINISHING A TOUR DOES NOT, AND TREATING
   * THAT AS "FOUND IT" WAS A BUG. A completed tour was latched as engagement, so from
   * the moment the visitor pressed Done the corner went quiet for the rest of the
   * session: a small static green circle over arbitrary page content, at exactly the
   * point they turn round looking for where to go next. The end of a tour is when the
   * widget matters MOST — it is the way back to the other tours, the plans and the
   * demo — so it goes back to waving.
   *
   * Nothing pulses DURING a tour either way: `running` still gates `attention` below,
   * and the dock is `display: none` for the duration anyway (the stage tracker owns
   * that corner). The difference is only what happens afterwards.
   *
   * 🔴 IT DELIBERATELY DOES NOT LOOK AT `tour.progress`, AND CHECKING IT WAS A BUG.
   * With `persist`, progress lives in `localStorage` under
   * `sandbox_tour_progress_v1`, so "has any tour ever been completed" is really
   * "has ANYONE ever completed a tour in this browser" — one finished tour switched
   * the wave off permanently, including for the next visitor on a shared demo
   * machine and for anyone who had merely clicked through it once. The wave is an
   * invitation to a control that is genuinely hard to spot (see above), and that
   * does not stop being true on a second visit; a returning visitor who never opens
   * it this time is exactly who it is for. Session state only — a reload brings it
   * back, which is the honest behaviour for something drawn per page load.
   *
   * 🔴 `data-attention` IS THE ONLY THING THIS ADDS TO THE DOM — no wrapper, no
   * extra element, and nothing that changes the group's box. The placement cascade
   * above hit-tests this very group, so a decorative node inside it (or a ring that
   * grew its rect) could be read as the header being "covered" and demote the whole
   * group to the corner. The wave is drawn with `box-shadow` on pseudo-elements
   * instead: shadows are never hit-tested and never affect layout.
   */
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (open) setEngaged(true);
  }, [open]);

  /**
   * 🔴 A FINISHED TOUR RE-ARMS THE WAVE, AND WITHOUT THIS THE FIX ABOVE ACHIEVES
   * NOTHING BELOW `lg`. In the widget, "Quick Tour" lives INSIDE the menu — so
   * reaching it means opening the widget, which is the one gesture that retires the
   * wave. Every visitor who takes the tour therefore arrives at Done already latched,
   * and the corner is quiet for the rest of the session: exactly the case being
   * fixed, just reached one step earlier.
   *
   * So the latch is undone when a run ENDS. Opening the menu and closing it again
   * without starting anything still means "found it, leave me alone" — but a tour
   * that ran and finished puts the visitor back at square one, looking for where to
   * go next, and the widget is the answer.
   *
   * An edge, not a level: it must fire on the true → false transition only. Reading
   * `!running` alone would clear the latch on every render while nothing is running,
   * i.e. always, which would make opening the menu stop quieting it at all.
   */
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (running) {
      wasRunningRef.current = true;
      return;
    }
    if (!wasRunningRef.current) return;
    wasRunningRef.current = false;
    setEngaged(false);
  }, [running]);
  const attention = enabled && !engaged && !open && !running && !pressing;

  const close = useCallback(() => setOpen(false), []);

  /* ---------------------------------------------------------------- *
   * Nudging the widget out of the way.
   * ---------------------------------------------------------------- *
   * An OFFSET from the measured home corner, applied as a transform — never a
   * replacement for `right`/`bottom`. `useDockBottom` keeps re-measuring the corner
   * (the host raises its own launcher over a pagination row, and removes it
   * entirely on some pages), so writing absolute coordinates here would fight that
   * measurement and pin the widget to a spot that stops being correct the moment
   * the page changes. A transform composes with it instead: the corner stays
   * authoritative, and this is the visitor's adjustment on top.
   *
   * Not persisted. It is a "get out of my way for a second" gesture, and a widget
   * that silently stayed 200px off-corner on the next visit would read as broken
   * placement rather than as a remembered preference.
   */

  /**
   * Clamp an offset to the allowed area: inside the radius AND inside the viewport.
   *
   * Measures the widget's CURRENT rect and subtracts the offset already applied to
   * get its home rect, so the limits are right whether the menu is open (210px
   * wide) or collapsed (a 48px button) — the open menu is what would otherwise be
   * dragged half off the left edge on a phone.
   */
  const clampOffset = useCallback((next: { x: number; y: number }) => {
    let { x, y } = next;

    const root = rootRef.current;
    if (root) {
      const rect = root.getBoundingClientRect();
      const homeLeft = rect.left - offset.x;
      const homeRight = rect.right - offset.x;
      const homeTop = rect.top - offset.y;
      const homeBottom = rect.bottom - offset.y;
      const m = DRAG_VIEWPORT_MARGIN;
      x = Math.min(Math.max(x, m - homeLeft), window.innerWidth - m - homeRight);
      y = Math.min(Math.max(y, m - homeTop), window.innerHeight - m - homeBottom);
    }

    // The radius last, so it can never be undone by a viewport clamp.
    const distance = Math.hypot(x, y);
    if (distance > DRAG_RADIUS_PX) {
      const scale = DRAG_RADIUS_PX / distance;
      x *= scale;
      y *= scale;
    }
    return { x: Math.round(x), y: Math.round(y) };
  }, [offset.x, offset.y]);

  const onFabPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    // Secondary buttons open context menus; they must not start a drag.
    if (event.button !== 0) return;
    setPressing(true);
    dragRef.current = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      fromX: offset.x,
      fromY: offset.y,
      moved: false,
    };
    // Capture, so a fast drag that leaves the 48px button keeps delivering moves.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [offset.x, offset.y]);

  const onFabPointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
    }
    setOffset(clampOffset({ x: drag.fromX + dx, y: drag.fromY + dy }));
  }, [clampOffset]);

  const onFabPointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    // A press that never moved is a tap: toggle here, in the same batch that lifts
    // the freeze, so `open` is already true when the cascade is allowed to run again.
    if (!drag.moved) setOpen((prev) => !prev);
    setPressing(false);
    setDragging(false);
  }, []);

  /**
   * Re-clamp when the shape or the room changes — the menu opening makes the widget
   * four times wider, and a resize can leave a previously-legal offset hanging off
   * the edge.
   */
  useEffect(() => {
    const reclamp = () => setOffset((prev) => {
      const next = clampOffset(prev);
      return next.x === prev.x && next.y === prev.y ? prev : next;
    });
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [open, dockBottom, clampOffset]);

  /**
   * A tour or flow takes the corner: drop the nudge, since the widget hides and its
   * next appearance should be where the measurement says it belongs.
   *
   * 🔴 DELIBERATELY NOT KEYED ON `dockable`. That flips false for a single commit
   * every time the cascade re-offers the header, so resetting on it wiped the offset
   * mid-drag — the widget snapped back to the corner on every prober bump, which at
   * 1024px and above is constant. The dock unmounting on a genuine move back to the
   * header makes the offset moot anyway, and the clamp effect above re-fits it if it
   * ever returns.
   */
  useEffect(() => {
    if (running) setOffset({ x: 0, y: 0 });
  }, [running]);

  // A tour or a guided flow owns the screen (and the bottom-right corner, where
  // the stage tracker sits) for its duration.
  useEffect(() => {
    if (running) close();
  }, [running, close]);

  /**
   * Going back to the header takes the widget away, so its open state must not
   * survive into the next visit.
   *
   * 🔴 ONLY A PLACEMENT THAT *STAYS* IN THE HEADER COUNTS, hence the delay. The
   * cascade re-offers the header OPTIMISTICALLY on every prober bump and takes it
   * back in the same pre-paint sequence when the measurement says there is no room,
   * so `dockable` false is far more often a transient than a decision. Closing on it
   * directly is what made the widget refuse to open at 1024px and above: the click
   * opened the menu, the very next cascade run flipped `dockable` for one commit,
   * and this effect shut it again before a frame was painted. 120ms is well past the
   * synchronous fallback and well short of anything a visitor would notice.
   */
  useEffect(() => {
    if (dockable) return;
    const settle = window.setTimeout(close, 120);
    return () => window.clearTimeout(settle);
  }, [dockable, close]);

  useEffect(() => {
    if (!dockable || !open) return;

    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dockable, open, close]);

  if (!dockable) {
    return (
      <HeaderSlot>
        <div
          ref={setGroupEl}
          className="tour-guide-actions tour-guide-actions--header"
          data-testid="tour-guide-actions"
          /*
           * 🔴 INVISIBLE UNTIL MEASURED, and `opacity` is the only way to do it:
           * `visibility: hidden` and `display: none` both take the element out of hit
           * testing, which is precisely what the cascade needs to perform.
           *
           * The measurement runs pre-paint, so in practice this is never seen — it
           * exists so that if a future React ever yields between the attempt and the
           * decision, the worst case is a group that appears one frame late rather
           * than one that blinks into the header and jumps to the corner. Rendered
           * only when the module is live: a disabled module never runs the effect
           * that clears it.
           */
          data-probing={enabled ? '' : undefined}
          data-attention={attention ? '' : undefined}
        >
          {children}
        </div>
      </HeaderSlot>
    );
  }

  return createPortal(
    <div
      ref={rootRef}
      className="tour-guide-dock"
      data-tour-dock=""
      data-open={open ? '' : undefined}
      data-attention={attention ? '' : undefined}
      // Hidden rather than unmounted: see the file header — unmounting would
      // take the demo dialog's owner with it.
      data-hidden={running ? '' : undefined}
      data-dragging={dragging ? '' : undefined}
      style={{
        bottom: dockBottom,
        // Composes with the measured corner rather than replacing it — see `offset`.
        transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px)` : undefined,
      }}
    >
      <div
        id="tour-guide-dock-menu"
        className="tour-guide-actions tour-guide-actions--dock"
        data-testid="tour-guide-actions"
        role="group"
        aria-label="Explore the WeNext sandbox"
        aria-hidden={open ? undefined : true}
        // Any action here either navigates, starts a tour or opens a dialog —
        // all of which make the menu itself redundant.
        onClick={close}
      >
        <span className="tour-guide-dock__title">Explore the sandbox</span>
        {children}
      </div>

      <button
        type="button"
        className="tour-guide-dock__fab"
        data-testid="tour-guide-dock-toggle"
        aria-expanded={open}
        aria-controls="tour-guide-dock-menu"
        aria-label={open ? 'Close sandbox actions' : 'Sandbox actions: quick tour, plans, demo'}
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        onPointerCancel={onFabPointerUp}
        onClick={(event) => {
          // Keyboard only — see the note on the pointer/keyboard split above.
          // `detail` is 0 for Enter/Space and >= 1 for anything a pointer produced.
          if (event.detail !== 0) return;
          setOpen((prev) => !prev);
        }}
      >
        {/*
          The name, on hover — the same treatment the header's icon-only variant
          gets, for the same reason: this is the one control in the dock that shows
          an icon and nothing else, so without it the only way to learn what the
          button does is to press it.

          🔴 A STYLED SPAN, NOT THE `title` ATTRIBUTE IT REPLACES. A native tooltip
          waits about a second, cannot be positioned, and would have to open to the
          RIGHT of a button that is already against the right edge of the viewport —
          so it was both slow and clipped. `aria-hidden` because the button's own
          `aria-label` is the accessible name; this text exists for sighted hover
          only and would otherwise be read out twice.
        */}
        <span className="tour-guide-dock__fab-label" aria-hidden="true">
          {open ? 'Close' : 'Quick tour, plans and demo'}
        </span>
        {/* Inlined so the module carries no icon-library dependency. */}
        {open ? (
          <svg
            className="tour-guide-dock__fab-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg
            className="tour-guide-dock__fab-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
        )}
      </button>
    </div>,
    document.body,
  );
}
